import { Fragment } from "react";
import {
  TreeNode,
  calculateOwnershipShares,
  getComplianceLabel,
  getRelationshipLabel,
  provideLegalName,
} from "@jsq/compliance-components";
import { Flex, HelpIndicator, Text } from "@jsq/ds/core";
import * as styles from "./styles_vanilla.css";
import type { OwnershipTreeEdge, OwnershipTreeProfile } from "./types";
import type { ProfileRelationshipEnum } from "~/graphql/supergraphTypes";

type HierarchyNode = {
  profile: OwnershipTreeProfile;
  relationships: Set<ProfileRelationshipEnum>;
  percentageOwnership: number | null;
  rootPercentageOwnership?: number | null;
  children: HierarchyNode[];
};

// Merge owner and controller edges per child to avoid duplicate tree nodes.
const collectChildHierarchy = (profile: OwnershipTreeProfile): HierarchyNode[] => {
  const byId = new Map<number, HierarchyNode>();

  const addEdges = (edges: (OwnershipTreeEdge | null)[] | null | undefined) => {
    for (const edge of edges ?? []) {
      const child = edge?.childProfile;
      if (!child) continue;
      const node = byId.get(child.id) ?? {
        profile: child,
        relationships: new Set<ProfileRelationshipEnum>(),
        percentageOwnership: null,
        children: [],
      };
      if (edge?.relationshipType) node.relationships.add(edge.relationshipType);
      if (edge?.percentOwnership != null) node.percentageOwnership = edge.percentOwnership;
      byId.set(child.id, node);
    }
  };

  addEdges(profile.beneficialOwners);
  addEdges(profile.controllingPersons);

  const nodes = [...byId.values()];
  for (const node of nodes) {
    node.children = collectChildHierarchy(node.profile);
  }
  return nodes;
};

const buildContent = (
  profile: OwnershipTreeProfile,
  relationships: Set<ProfileRelationshipEnum>,
) => {
  const name = provideLegalName(profile);
  const subParts = [
    ...[...relationships].map((relationship) => getRelationshipLabel(relationship)),
    getComplianceLabel(profile.complianceObjectType),
  ].filter((label): label is string => Boolean(label));

  return (
    <Flex className={styles.nodeCard}>
      <Text variant="strong-lg" className={styles.headerText} title={name}>
        {name}
      </Text>
      {subParts.length > 0 && (
        <Flex className={styles.subHeaderRow}>
          {subParts.map((part, index) => (
            <Fragment key={part}>
              {index > 0 && (
                <Text variant="secondary-sm" className={styles.subHeaderDivider}>
                  |
                </Text>
              )}
              <Text variant="secondary-sm">{part}</Text>
            </Fragment>
          ))}
        </Flex>
      )}
    </Flex>
  );
};

const buildEdgeLabel = (
  percentOwnership: number | null | undefined,
  variant: "direct" | "root",
  id: string,
) => {
  if (percentOwnership == null) return undefined;
  return (
    <HelpIndicator content={`${percentOwnership.toFixed(2)}%`} id={id}>
      <Text
        variant="strong-sm"
        className={variant === "root" ? styles.rootEdgeLabel : styles.edgeLabel}
      >
        {`${Math.round(percentOwnership)}%`}
      </Text>
    </HelpIndicator>
  );
};

const toTreeNode = (node: HierarchyNode, parentId: string): TreeNode => {
  // Path-specific id so the same profile appearing in multiple branches doesn't collide.
  const nodeId = `${parentId}/${node.profile.id}`;
  return {
    id: nodeId,
    parentId,
    content: buildContent(node.profile, node.relationships),
    edgeLabel: buildEdgeLabel(node.percentageOwnership, "direct", `edge-${nodeId}`),
    rootEdgeLabel: buildEdgeLabel(node.rootPercentageOwnership, "root", `root-edge-${nodeId}`),
    leafNodes: node.children.map((child) => toTreeNode(child, nodeId)),
  };
};

export const buildTreeData = (
  rootProfile: OwnershipTreeProfile | null | undefined,
): TreeNode | null => {
  if (!rootProfile) return null;

  const rootChildren = collectChildHierarchy(rootProfile);
  calculateOwnershipShares(rootChildren);

  const rootId = String(rootProfile.id);
  return {
    id: rootId,
    content: buildContent(rootProfile, new Set<ProfileRelationshipEnum>()),
    leafNodes: rootChildren.map((child) => toTreeNode(child, rootId)),
  };
};
